import React, { Fragment } from 'react';
import moment from 'moment-timezone';
import { I18n, Translate } from 'react-redux-i18n';
import ModernDatepicker from 'react-modern-datepicker';

import AnalyticsServer from '../../../app/server/analytics';
import {
  analyticsTodayDate,
  defaultAnalyticsDateRange,
  formatAnalyticsApiDate,
  onEndDateChange,
  onStartDateChange,
  parseDatepickerChange,
} from '../utils/dateRange';
import {
  AnalyticsBarList,
  AnalyticsKpiGrid,
  AnalyticsPanel,
  formatKpiValue,
} from './AnalyticsVisuals';

const TZ = 'America/Santiago';
const MAX_DATE = analyticsTodayDate();

const OVERVIEW_KPIS = [
  { key: 'activeUsers', label: 'analyticsActiveUsers', format: 'number' },
  { key: 'sessions', label: 'analyticsSessions', format: 'number' },
  { key: 'pageViews', label: 'analyticsPageViews', format: 'number' },
  { key: 'viewItem', label: 'analyticsViewItem', format: 'number' },
  { key: 'addToCart', label: 'analyticsAddToCart', format: 'number' },
  { key: 'beginCheckout', label: 'analyticsBeginCheckout', format: 'number' },
  { key: 'ecommercePurchases', label: 'analyticsPurchases', format: 'number' },
  { key: 'purchaseRevenue', label: 'analyticsPurchaseRevenue', format: 'revenue' },
  { key: 'login', label: 'analyticsLogin', format: 'number' },
  { key: 'signUp', label: 'analyticsSignUp', format: 'number' },
  { key: 'refunds', label: 'analyticsRefunds', format: 'number' },
  { key: 'refundRevenue', label: 'analyticsRefundRevenue', format: 'revenue' },
];

const KPI_BY_KEY = OVERVIEW_KPIS.reduce((acc, kpi) => {
  acc[kpi.key] = kpi;
  return acc;
}, {});

const BREAKDOWN_OPTIONS = [
  { value: 'none', label: 'analyticsReportsBreakdownNone' },
  { value: 'top_pages', label: 'analyticsReportsBreakdownTopPages' },
  { value: 'top_items', label: 'analyticsReportsBreakdownTopItems' },
  { value: 'auth_method', label: 'analyticsReportsBreakdownAuth' },
];

const RANGE_OPTIONS = [7, 14, 30];

const AUTH_EVENT_LABELS = {
  login: 'analyticsLogin',
  sign_up: 'analyticsSignUp',
};

const EMPTY_FORM = {
  id: null,
  name: '',
  metrics: [],
  breakdown: 'none',
  defaultRangeDays: 7,
};

function defaultRunRange(days) {
  return defaultAnalyticsDateRange((days || 7) - 1);
}

function formatApiDate(date) {
  return formatAnalyticsApiDate(date);
}

function authEventLabel(eventName) {
  const key = AUTH_EVENT_LABELS[eventName];
  return key ? I18n.t(key) : eventName.replace(/_/g, ' ');
}

class ReportsTab extends React.PureComponent {
  constructor(props) {
    super(props);
    const { startDate, endDate } = defaultRunRange(7);
    this.state = {
      mode: 'list',
      list: [],
      loading: false,
      errorMessage: '',
      form: { ...EMPTY_FORM },
      run: {
        reportId: null,
        reportName: '',
        defaultRangeDays: 7,
        startDate,
        endDate,
        payload: null,
      },
    };
  }

  componentDidMount() {
    this.loadList();
  }

  loadList = async () => {
    this.setState({ loading: true, errorMessage: '' });
    try {
      const list = await AnalyticsServer.listSavedReports();
      this.setState({ list: list || [], loading: false });
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({
        loading: false,
        errorMessage: (error && error.message) || I18n.t('unknownError'),
      });
    }
  };

  openNewForm = () => {
    this.setState({
      mode: 'form',
      errorMessage: '',
      form: { ...EMPTY_FORM },
    });
  };

  openEditForm = (item) => {
    const def = item.definition || {};
    this.setState({
      mode: 'form',
      errorMessage: '',
      form: {
        id: item.id,
        name: item.name || '',
        metrics: Array.isArray(def.metrics) ? [...def.metrics] : [],
        breakdown: def.breakdown || 'none',
        defaultRangeDays: def.defaultRangeDays || 7,
      },
    });
  };

  openRun = (item) => {
    const def = item.definition || {};
    const days = def.defaultRangeDays || 7;
    const { startDate, endDate } = defaultRunRange(days);
    this.setState({
      mode: 'run',
      errorMessage: '',
      run: {
        reportId: item.id,
        reportName: item.name || '',
        defaultRangeDays: days,
        startDate,
        endDate,
        payload: null,
      },
    }, () => this.executeRun());
  };

  backToList = () => {
    this.setState({ mode: 'list', errorMessage: '' }, () => this.loadList());
  };

  handleDelete = async (item) => {
    if (!window.confirm(I18n.t('analyticsReportsDeleteConfirm'))) {
      return;
    }
    this.setState({ loading: true, errorMessage: '' });
    try {
      await AnalyticsServer.deleteSavedReport(item.id);
      await this.loadList();
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({
        loading: false,
        errorMessage: (error && error.message) || I18n.t('unknownError'),
      });
    }
  };

  toggleMetric = (key) => {
    this.setState((prev) => {
      const metrics = prev.form.metrics.includes(key)
        ? prev.form.metrics.filter((m) => m !== key)
        : [...prev.form.metrics, key];
      return { form: { ...prev.form, metrics } };
    });
  };

  handleFormChange = (field, value) => {
    this.setState((prev) => ({
      form: { ...prev.form, [field]: value },
    }));
  };

  handleSave = async () => {
    const { form } = this.state;
    const trimmed = (form.name || '').trim();
    if (!trimmed) {
      this.setState({ errorMessage: I18n.t('analyticsReportsNameRequired') });
      return;
    }
    if (!form.metrics.length) {
      this.setState({ errorMessage: I18n.t('analyticsReportsMetricsRequired') });
      return;
    }

    const body = {
      name: trimmed,
      definition: {
        version: 1,
        metrics: form.metrics,
        breakdown: form.breakdown,
        defaultRangeDays: form.defaultRangeDays,
      },
    };

    this.setState({ loading: true, errorMessage: '' });
    try {
      if (form.id) {
        await AnalyticsServer.updateSavedReport(form.id, body);
      } else {
        await AnalyticsServer.createSavedReport(body);
      }
      this.setState({ mode: 'list', loading: false }, () => this.loadList());
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({
        loading: false,
        errorMessage: (error && error.message) || I18n.t('unknownError'),
      });
    }
  };

  executeRun = async () => {
    const { run } = this.state;
    const start = formatApiDate(run.startDate);
    const end = formatApiDate(run.endDate);

    this.setState({ loading: true, errorMessage: '', run: { ...run, payload: null } });
    try {
      const payload = await AnalyticsServer.runSavedReport(run.reportId, start, end);
      this.setState((prev) => ({
        loading: false,
        run: { ...prev.run, payload },
      }));
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({
        loading: false,
        errorMessage: (error && error.message) || I18n.t('unknownError'),
      });
    }
  };

  renderList = () => {
    const { list, loading } = this.state;

    return (
      <Fragment>
        <div className="margin-top-20">
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading}
            onClick={this.openNewForm}
          >
            <Translate value="analyticsReportsNew" />
          </button>
        </div>

        {loading && !list.length && (
          <p className="margin-top-20">
            <Translate value="analyticsLoading" />
          </p>
        )}

        {!loading && !list.length && (
          <div className="analytics-empty">
            <p>
              <Translate value="analyticsReportsEmptyList" />
            </p>
          </div>
        )}

        {list.length > 0 && (
          <table className="table table-striped margin-top-20">
            <thead className="thead-dark">
              <tr>
                <th scope="col">
                  <Translate value="analyticsReportsName" />
                </th>
                <th scope="col">
                  <Translate value="analyticsUpdatedAt" />
                </th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>
                    {item.updatedAt
                      ? moment(item.updatedAt).tz(TZ).format('L LT')
                      : '—'}
                  </td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary margin-right-5"
                      disabled={loading}
                      onClick={() => this.openRun(item)}
                    >
                      <Translate value="analyticsReportsOpen" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-default margin-right-5"
                      disabled={loading}
                      onClick={() => this.openEditForm(item)}
                    >
                      <Translate value="editar" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={loading}
                      onClick={() => this.handleDelete(item)}
                    >
                      <Translate value="delete" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Fragment>
    );
  };

  renderForm = () => {
    const { form, loading } = this.state;
    const titleKey = form.id ? 'analyticsReportsEdit' : 'analyticsReportsNew';

    return (
      <div className="margin-top-20">
        <h4>
          <Translate value={titleKey} />
        </h4>

        <div className="form-group margin-top-20">
          <label htmlFor="report-name">
            <Translate value="analyticsReportsName" />
          </label>
          <input
            id="report-name"
            type="text"
            className="form-control"
            value={form.name}
            onChange={(e) => this.handleFormChange('name', e.target.value)}
          />
        </div>

        <div className="form-group margin-top-20">
          <p className="margin-bottom-2">
            <Translate value="analyticsReportsMetrics" />
          </p>
          <div className="row">
            {OVERVIEW_KPIS.map(({ key, label }) => (
              <div className="col-lg-4 col-md-6" key={key}>
                <label className="checkbox-inline">
                  <input
                    type="checkbox"
                    checked={form.metrics.includes(key)}
                    onChange={() => this.toggleMetric(key)}
                  />
                  {' '}
                  <Translate value={label} />
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="row margin-top-20">
          <div className="col-lg-6">
            <div className="form-group">
              <label htmlFor="report-breakdown">
                <Translate value="analyticsReportsBreakdown" />
              </label>
              <select
                id="report-breakdown"
                className="form-control"
                value={form.breakdown}
                onChange={(e) => this.handleFormChange('breakdown', e.target.value)}
              >
                {BREAKDOWN_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {I18n.t(label)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="form-group">
              <label htmlFor="report-range">
                <Translate value="analyticsReportsDefaultRange" />
              </label>
              <select
                id="report-range"
                className="form-control"
                value={form.defaultRangeDays}
                onChange={(e) => this.handleFormChange(
                  'defaultRangeDays',
                  Number(e.target.value),
                )}
              >
                {RANGE_OPTIONS.map((days) => (
                  <option key={days} value={days}>
                    {I18n.t('analyticsReportsRangeDays', { count: days })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="margin-top-20">
          <button
            type="button"
            className="btn btn-primary margin-right-5"
            disabled={loading}
            onClick={this.handleSave}
          >
            <Translate value="analyticsReportsSave" />
          </button>
          <button
            type="button"
            className="btn btn-default"
            disabled={loading}
            onClick={this.backToList}
          >
            <Translate value="analyticsReportsCancel" />
          </button>
        </div>
      </div>
    );
  };

  renderRunKpis = (kpis) => (
    <div className="margin-top-20">
      <AnalyticsKpiGrid
        items={Object.keys(kpis).map((key) => {
          const meta = KPI_BY_KEY[key];
          if (!meta) return null;
          return {
            key,
            labelKey: meta.label,
            value: formatKpiValue(kpis[key], meta.format),
          };
        }).filter(Boolean)}
      />
    </div>
  );

  renderTopPagesBreakdown = (rows) => {
    if (!rows || !rows.length) {
      return (
        <div className="analytics-empty">
          <p>
            <Translate value="analyticsEmpty" />
          </p>
        </div>
      );
    }

    return (
      <AnalyticsPanel titleKey="analyticsTopPages">
        <AnalyticsBarList
          rows={rows.map((row) => ({
            key: row.pagePath,
            label: row.pagePath,
            value: row.screenPageViews,
          }))}
          tone="teal"
        />
      </AnalyticsPanel>
    );
  };

  renderTopItemsBreakdown = (rows) => {
    if (!rows || !rows.length) {
      return (
        <div className="analytics-empty">
          <p>
            <Translate value="analyticsEmpty" />
          </p>
        </div>
      );
    }

    return (
      <AnalyticsPanel titleKey="analyticsTopItems">
        <AnalyticsBarList
          rows={rows.map((row) => ({
            key: row.itemId,
            label: row.itemName || row.itemId,
            value: row.purchaseCount || row.viewItemCount || 0,
          }))}
          tone="orange"
        />
        <table className="table table-striped margin-top-20">
          <thead className="thead-dark">
            <tr>
              <th scope="col">
                <Translate value="analyticsItem" />
              </th>
              <th scope="col">
                <Translate value="analyticsViewItem" />
              </th>
              <th scope="col">
                <Translate value="analyticsAddToCart" />
              </th>
              <th scope="col">
                <Translate value="analyticsPurchases" />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.itemId}>
                <td>{row.itemName || row.itemId}</td>
                <td>{row.viewItemCount}</td>
                <td>{row.addToCartCount}</td>
                <td>{row.purchaseCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AnalyticsPanel>
    );
  };

  renderAuthBreakdown = (rows) => {
    if (!rows || !rows.length) {
      return (
        <div className="analytics-empty">
          <p>
            <Translate value="analyticsEmpty" />
          </p>
        </div>
      );
    }

    return (
      <AnalyticsPanel titleKey="analyticsAuthMethods">
        <AnalyticsBarList
          rows={rows.map((row) => ({
            key: `${row.eventName}-${row.method}`,
            label: `${authEventLabel(row.eventName)} · ${row.method}`,
            value: row.eventCount,
          }))}
        />
      </AnalyticsPanel>
    );
  };

  renderBreakdown = (breakdown) => {
    if (!breakdown || breakdown.type === 'none') {
      return null;
    }

    if (breakdown.empty) {
      return (
        <p className="margin-top-20">
          <Translate value="analyticsEmpty" />
        </p>
      );
    }

    if (breakdown.type === 'top_pages') {
      return this.renderTopPagesBreakdown(breakdown.rows);
    }

    if (breakdown.type === 'top_items') {
      return this.renderTopItemsBreakdown(breakdown.rows);
    }

    if (breakdown.type === 'auth_method') {
      return this.renderAuthBreakdown(breakdown.rows);
    }

    return null;
  };

  renderRun = () => {
    const { run, loading } = this.state;
    const { payload } = run;

    return (
      <div className="margin-top-20">
        <h4>{run.reportName}</h4>

        <div className="row margin-top-20">
          <div className="col-lg-6">
            <div className="form-group form-promotion-picker">
              <p className="margin-bottom-2">{I18n.t('initialDate')}</p>
              <div className="inputs">
                <ModernDatepicker
                  className="form-control"
                  showBorder
                  placeholder=""
                  format="DD/MM/YYYY"
                  date={run.startDate}
                  maxDate={MAX_DATE}
                  onChange={(date) => {
                    const nextStart = parseDatepickerChange(date);
                    const { startDate, endDate } = onStartDateChange(nextStart, run.endDate);
                    this.setState((prev) => ({
                      run: { ...prev.run, startDate, endDate },
                    }));
                  }}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="form-group form-promotion-picker">
              <p className="margin-bottom-2">{I18n.t('finalDate')}</p>
              <div className="inputs">
                <ModernDatepicker
                  className="form-control"
                  showBorder
                  placeholder=""
                  format="DD/MM/YYYY"
                  date={run.endDate}
                  maxDate={MAX_DATE}
                  onChange={(date) => {
                    const nextEnd = parseDatepickerChange(date);
                    const { startDate, endDate } = onEndDateChange(run.startDate, nextEnd);
                    this.setState((prev) => ({
                      run: { ...prev.run, startDate, endDate },
                    }));
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="btn-group margin-top-10" role="group">
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading}
            onClick={this.executeRun}
          >
            {I18n.t('generateReport')}
          </button>
          <button
            type="button"
            className="btn btn-default margin-left-5"
            disabled={loading}
            onClick={this.backToList}
          >
            <Translate value="analyticsReportsCancel" />
          </button>
        </div>

        {loading && !payload && (
          <p className="margin-top-20">
            <Translate value="analyticsLoading" />
          </p>
        )}

        {payload && payload.empty && (
          <p className="margin-top-20">
            <Translate value="analyticsReportsRunEmpty" />
          </p>
        )}

        {payload && !payload.empty && payload.kpis && (
          <Fragment>
            {payload.extractedAt && (
              <p className="margin-top-20">
                {I18n.t('analyticsUpdatedAt')}
                {': '}
                {moment(payload.extractedAt).tz(TZ).format('L LT')}
              </p>
            )}
            {this.renderRunKpis(payload.kpis)}
            {this.renderBreakdown(payload.breakdown)}
          </Fragment>
        )}
      </div>
    );
  };

  render() {
    const { mode, errorMessage } = this.state;

    return (
      <div className="analytics-page">
        {errorMessage && (
          <p className="color-red margin-top-20">{errorMessage}</p>
        )}

        {mode === 'list' && this.renderList()}
        {mode === 'form' && this.renderForm()}
        {mode === 'run' && this.renderRun()}
      </div>
    );
  }
}

export default ReportsTab;
